'use client'

import { useCallback, useContext, useRef, type ComponentProps, type MouseEvent } from 'react'
import { shopNextPath } from '@/shared/shop-site'
import { leaveSite } from '@/shared/utilities/leave-site.utility'
import { mintShopHandoff, type ShopHandoffResult } from '../actions/auth.actions'
import { AuthContext } from '../state/auth-provider'

/**
 * Redeems a minted Handoff (ADR-0150): a form on this document, POSTed to
 * the issuer's redeem endpoint with the code and the Shop path to land on.
 * A form, because the redeem is a POST and only a POST — a code must never
 * ride a link — and on this document, because Go admits the post only from
 * the website's own origin (the browser sends it as `Origin`, and marks a
 * form from any other site `Sec-Fetch-Site: cross-site`). Go burns the code,
 * sets the Handoff cookie and sends the browser to the Shop's sign-in route,
 * where Continue with HubHive completes without a tap.
 */
function redeemHandoff(handoff: Extract<ShopHandoffResult, { ok: true }>): void {
  const form = document.createElement('form')
  form.method = 'post'
  form.action = handoff.redeemUrl
  form.hidden = true
  const fields: Array<[string, string]> = [['code', handoff.code]]
  if (handoff.next) fields.push(['next', handoff.next])
  for (const [name, value] of fields) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.appendChild(input)
  }
  document.body.appendChild(form)
  form.submit()
}

/**
 * Opens a Shop URL the way ADR-0150 wants: a signed-in visitor goes through
 * a Handoff code, minted by a server action with the site session, and lands
 * in the Shop signed in; a signed-out visitor, a URL that is not the Shop's,
 * or a handoff Go refused takes the plain navigation and lands signed out.
 * The code never appears in a URL, a log or client state: it goes from the
 * action's answer straight into the form that posts it.
 */
export function useShopHandoff() {
  // Read, not required: a Shop link rendered outside the app's providers is
  // still a link, and a visitor it cannot vouch for is treated as signed out.
  const isAuthenticated = useContext(AuthContext)?.isAuthenticated ?? false
  const pending = useRef(false)

  const openShop = useCallback(
    async (href: string) => {
      const next = shopNextPath(href)
      if (!isAuthenticated || next === null) {
        leaveSite(href)
        return
      }
      // A second click while the first is minting would mint a second code
      // and submit twice; the first wins.
      if (pending.current) return
      pending.current = true
      try {
        const handoff = await mintShopHandoff(next).catch((): ShopHandoffResult => ({ ok: false }))
        if (handoff.ok) redeemHandoff(handoff)
        else leaveSite(href)
      } finally {
        pending.current = false
      }
    },
    [isAuthenticated]
  )

  return { openShop, signedIn: isAuthenticated }
}

type ShopLinkProps = Omit<ComponentProps<'a'>, 'href'> & { href: string }

/**
 * A link to the HubHive Shop. Renders the plain anchor everyone has always
 * had — same markup signed in or out, so nothing differs between server and
 * client render, and a copied or middle-clicked link is still the Shop URL.
 * For a signed-in visitor a plain left click is taken over and goes through
 * the Handoff (see useShopHandoff); a modified click, another button, a
 * link that opens elsewhere, or a URL off the Shop origin keeps the browser's
 * own navigation.
 */
export function ShopLink({ href, onClick, children, ...rest }: ShopLinkProps) {
  const { openShop, signedIn } = useShopHandoff()

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event)
    if (event.defaultPrevented || !signedIn) return
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    if (rest.target && rest.target !== '_self') return
    if (shopNextPath(href) === null) return
    event.preventDefault()
    void openShop(href)
  }

  return (
    <a {...rest} href={href} onClick={handleClick}>
      {children}
    </a>
  )
}
